import React from "react";
import HoverTip from "@/components/HoverTip";

export const scoreColor = (v) => {
    if (v == null) return "#9CA3AF";
    if (v >= 75) return "#1E7D45";
    if (v >= 50) return "#B8860B";
    return "#B32A22";
};

export const tamColor = (v) => (v == null ? "#9CA3AF" : v >= 1 ? "#1E7D45" : "#B8860B");

export const coefColor = (c) => (c == null ? "#9CA3AF" : c > 1.05 ? "#1D7044" : c < 0.95 ? "#B32A22" : "#B8860B");

export const fmtCoef = (v) => (v == null || isNaN(v) ? null : v.toFixed(2));

export const fmtTamScore = (v) => {
    if (v == null || isNaN(v)) return null;
    return v.toFixed(2);
};

/** Square boxed value, matching the ScoreBadge box look. size: sm | lg */
export function ValueBox({ text, color, testid, title, size = "sm" }) {
    const cls = size === "lg" ? "w-12 h-12 text-sm sm:w-16 sm:h-14 sm:text-base" : "w-11 h-10 text-xs";
    return (
        <div
            className={`${cls} border-2 flex items-center justify-center font-mono font-bold shrink-0`}
            style={{ borderColor: color, color }}
            data-testid={testid}
            title={title}
        >
            {text == null ? "—" : text}
        </div>
    );
}

const DIM_LABELS = {
    competitive_position: "Posición competitiva",
    sector_momentum: "Momentum del sector",
    management_quality: "Calidad del management",
    financial_resilience: "Resiliencia financiera",
    trend_exposure: "Exposición a la tendencia",
};

// What each of the five scores of a company in a trend thesis means (0-100, higher = better).
// The «Score tesis» is the simple mean of the four notes (the exposure is informative).
const DIM_TIPS = {
    competitive_position: "Posición competitiva: lo bien situada que está la empresa en SU eslabón de esta tendencia — cuota de mercado, tecnología, clientes y ventajas frente a sus rivales directos.",
    sector_momentum: "Momentum del sector: la fuerza actual de la demanda y de la inversión en ese eslabón de la tendencia. Es parecida para todas las empresas del mismo eslabón.",
    management_quality: "Calidad del management: ejecución, asignación de capital e historial de la dirección. Es de la empresa entera, así que suele repetirse en todas sus tesis.",
    financial_resilience: "Resiliencia financiera: balance, caja, deuda y generación de caja para sostener esta apuesta. Es de la empresa entera, así que suele repetirse en todas sus tesis.",
    trend_exposure: "Exposición a la tendencia: qué parte aproximada del valor o del negocio de la empresa depende de esta tendencia (100 = empresa pura de la tendencia; 5-15 = un nicho menor dentro de un negocio mucho mayor). Mide cuánto se juega la empresa en ella. No entra en el Score de esta tesis: pondera la tesis en el Score media de la empresa (el negocio principal cuenta entero y una apuesta pequeña, en proporción). No afecta al TAM Score.",
};

export function ScoreBadge({ value, label, testid }) {
    const c = scoreColor(value);
    return (
        <div className="flex items-center gap-2" data-testid={testid}>
            <div
                className="w-10 h-10 border-2 flex items-center justify-center font-mono text-sm font-bold shrink-0"
                style={{ borderColor: c, color: c }}
            >
                {value == null ? "—" : value}
            </div>
            {label && <span className="overline text-[#4A4A4A] leading-tight">{label}</span>}
        </div>
    );
}

export function ScoreBar({ dimension, value }) {
    const c = scoreColor(value);
    const label = DIM_LABELS[dimension] || dimension;
    const tip = DIM_TIPS[dimension];
    return (
        <div className="space-y-1" data-testid={`score-bar-${dimension}`}>
            <div className="flex justify-between items-center gap-2">
                {tip
                    ? <HoverTip text={tip} maxWidth={300}><span className="text-xs text-[#4A4A4A] cursor-help underline decoration-dotted decoration-[#9CA3AF] underline-offset-2">{label}</span></HoverTip>
                    : <span className="text-xs text-[#4A4A4A]">{label}</span>}
                <span className="font-mono text-xs font-semibold" style={{ color: c }}>
                    {value == null ? "—" : value}
                </span>
            </div>
            <div className="h-1.5 w-full bg-black/10">
                <div className="h-full transition-all" style={{ width: `${value || 0}%`, background: c }} />
            </div>
        </div>
    );
}
